import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import type { IDomainPickupOrder } from "~/domain/pickup-order";
import type { IDomainDeliveryOrder } from "~/domain/delivery-order";
import type { IDomainCurbsideOrder } from "~/domain/curbside-order";
import type { IDomainDineInOrder } from "~/domain/dine-in-order";
import { useDirectBackend } from "#imports";

// Define the state interface
type IState = (
  | IDomainPickupOrder
  | IDomainDeliveryOrder
  | IDomainCurbsideOrder
  | IDomainDineInOrder
) & {
  isLoaded: boolean;
  isProcessing: boolean;
};

// Helper function to create the initial state
function createInitialState(): IState {
  const ZERO = { value: 0, currency: "AED" };

  return {
    isLoaded: false,
    isProcessing: false,
    id: "",
    trackingId: "",
    uid: "",
    orderingMode: "delivery",
    items: [],
    paymentMethod: undefined,
    outlet: {
      id: "",
      name: "",
      slug: "",
      address: {
        city: "",
        location: "",
        coordinate: {
          lat: 0,
          lng: 0,
        },
      },
      isTaxIncluded: false,
    },
    createdAt: new Date(),
    scheduledTo: undefined,
    notes: "",
    status: "created",
    total: ZERO,
    totalBreakdown: {
      serviceCharges: ZERO,
      bagFee: ZERO,
      loyaltyRedeemed: ZERO,
      taxAmount: ZERO,
      deliveryFee: ZERO,
      discountedAmount: ZERO,
      productTotal: ZERO,
      subtotal: ZERO,
      tip: ZERO,
    },
    payments: [],
    deliveryAddress: {
      id: "",
      nickname: "",
      location: "",
      coordinate: { lat: 0, lng: 0 },
      zipCode: undefined,
      number: undefined,
    },
    deliveryEstimatedAt: undefined,
    trackingLink: undefined,
    preparationEstimatedAt: undefined,
    // @ts-expect-error mapped for pickup mode
    packagingType: undefined,
    vehicle: undefined,
  };
}

export const useOrderStore = defineStore("order", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  const isPickup = computed(() => state.orderingMode === "pickup");

  const isDelivery = computed(() => state.orderingMode === "delivery");

  const isCurbside = computed(() => state.orderingMode === "curbside");

  const { orderRepo } = useDirectBackend();

  async function refresh() {
    await load(state.id, true);
  }

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  async function loadFromTrackingId(trackingId: string, forceRefresh = false) {
    if (!forceRefresh && state.isLoaded && state.trackingId === trackingId) {
      // order is already loaded
      return;
    }

    state.isProcessing = true;

    let orderId;

    try {
      const res = await orderRepo.getByTrackingId({ trackingId });
      orderId = res.id;

      await load(orderId, forceRefresh);
    } catch (err) {
      state.isProcessing = false;
      throw err;
    }
  }

  async function load(id: string, forceRefresh = false) {
    if (!forceRefresh && state.isLoaded && state.id === id) {
      // order is already loaded
      return;
    }

    state.isProcessing = true;

    try {
      const data = await orderRepo.getById({ orderId: id });

      patch({
        id: data.id,
        uid: data.uid,
        trackingId: data.trackingId,
        status: data.status,
        items: data.items,
        paymentMethod: data.paymentMethod,
        notes: data.notes,
        createdAt: new Date(data.createdAt),
        scheduledTo: data.scheduledTo ? new Date(data.scheduledTo) : undefined,
        total: data.total,
        totalBreakdown: data.totalBreakdown,
        outlet: data.outlet,
        payments: data.payments,
      });

      if (data.orderingMode === "delivery") {
        patch({
          orderingMode: "delivery",
          deliveryAddress: data.deliveryAddress,
          trackingLink: data.trackingLink,
          deliveryEstimatedAt: data.deliveryEstimatedAt
            ? new Date(data.deliveryEstimatedAt)
            : undefined,
        });
      }

      if (data.orderingMode === "pickup") {
        patch({
          orderingMode: "pickup",
          preparationEstimatedAt: data.preparationEstimatedAt
            ? new Date(data.preparationEstimatedAt)
            : undefined,
          ...(data.packagingType && { packagingType: data.packagingType }),
        });
      }

      if (data.orderingMode === "curbside") {
        patch({
          orderingMode: "curbside",
          preparationEstimatedAt: data.preparationEstimatedAt
            ? new Date(data.preparationEstimatedAt)
            : undefined,
          vehicle: data.vehicle,
        });
      }

      if (data.orderingMode === "dine-in") {
        patch({
          orderingMode: "dine-in",
          preparationEstimatedAt: data.preparationEstimatedAt
            ? new Date(data.preparationEstimatedAt)
            : undefined,
        });
      }

      state.isLoaded = true;
    } finally {
      state.isProcessing = false;
    }
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    isPickup,
    isDelivery,
    isCurbside,
    patch,
    reset,
    refresh,
    loadFromTrackingId,
    load,
  };
});
